import React, { useMemo } from 'react';
import { HbrDomTable } from '@harbor/elements-react-standalone';

import type { AttainmentPeriodElement } from '../../../hooks/attainment-hooks';
import {
  Column,
  ColumnAlignment,
  ColumnType,
  DataType,
  getColumnTypeConfig,
} from '../../../types/table';
import { attainmentPct, formatUsdCompact } from '../../../support/enablementLeader';
import {
  elementBookingsMixRows,
  elementLabel,
  elementRows,
  elementsTotal,
} from '../../../support/attainmentLeader';
import CampaignChartCard, { ChartGrid, DASH, KpiCards } from '../Campaigns/CampaignChartCard';

/**
 * Plan-element breakdown (SEC-GROWTH-ACV|PRI, SEC-TTLSEC-S-XA|NPR …) for one interval: a card per element,
 * quota / bookings / attainment charts, the bookings mix, and a table with a total row. Used for a person's
 * own elements and for a team's subtree-summed elements.
 */

interface Props {
  elements: AttainmentPeriodElement[];
  /** Unique per usage, so chart tiles don't collide. */
  keyPrefix: string;
  /** Interval label for chart subtitles. */
  intervalLabel: string;
  empty?: string;
}

const HBAR = {
  valueFormat: 'number',
  axisLeftLegend: 'Plan element',
  axisLeftLegendOffset: -204,
  marginLeft: 220,
  maxLeftMarginRatio: 0.5,
};

type Row = {
  id: string;
  element: string;
  type: string;
  primary: string;
  quota: string;
  bookings: string;
  attainment: string;
  isTotal: boolean;
};

const pctText = (v: number | null) => (v == null ? '—' : `${v}%`);

export default function AttainmentElementsSection({ elements, keyPrefix, intervalLabel, empty }: Props) {
  const rows = useMemo<Row[]>(() => {
    const body = elements.map((el, i) => ({
      id: `el_${i}`,
      element: elementLabel(el),
      type: el.type || '—',
      primary: el.primary ? 'Yes' : '—',
      quota: formatUsdCompact(el.quota),
      bookings: formatUsdCompact(el.bookings),
      attainment: pctText(attainmentPct(el)),
      isTotal: false,
    }));
    const t = elementsTotal(elements);
    return [
      ...body,
      {
        id: 'el_total',
        element: 'Total',
        type: '',
        primary: '',
        quota: formatUsdCompact(t.quota),
        bookings: formatUsdCompact(t.bookings),
        attainment: pctText(attainmentPct(t)),
        isTotal: true,
      },
    ];
  }, [elements]);

  const columns: Column[] = useMemo(() => {
    const custom = (id: string, name: string, width: string, align: ColumnAlignment): Column => ({
      id,
      name,
      width,
      columnType: ColumnType.Custom,
      sortable: false,
      align,
      dataType: DataType.String,
      resizable: false,
      reorderable: false,
    });
    return [
      custom('element', 'Plan Element', '34%', ColumnAlignment.Left),
      custom('type', 'Type', '14%', ColumnAlignment.Left),
      custom('primary', 'Primary', '10%', ColumnAlignment.Left),
      custom('quota', 'Quota', '14%', ColumnAlignment.Right),
      custom('bookings', 'Bookings', '14%', ColumnAlignment.Right),
      custom('attainment', 'Attainment %', '14%', ColumnAlignment.Right),
    ]
      .map((c) => getColumnTypeConfig(c))
      .map((c) => ({ ...c, resizable: false, reorderable: false }));
  }, []);

  if (!elements.length) {
    return (
      <div className="hbr-css__text-secondary-sm">
        {empty || `No plan elements for ${intervalLabel || 'this interval'}.`}
      </div>
    );
  }

  const sub = (what: string) => `${intervalLabel || 'Interval'} (${what})`;

  return (
    <div>
      <KpiCards
        kpis={elements.map((el) => ({
          label: `${elementLabel(el)}${el.type ? ` · ${el.type}` : ''}${el.primary ? ' · primary' : ''}`,
          value: (
            <>
              {pctText(attainmentPct(el))}
              <div style={{ fontSize: '0.75rem', fontWeight: 500, color: DASH.textDim, marginTop: 2 }}>
                {formatUsdCompact(el.bookings)} of {formatUsdCompact(el.quota)}
              </div>
            </>
          ),
        }))}
      />
      <ChartGrid>
        <CampaignChartCard
          title="Attainment by plan element"
          subtitle={sub('% of quota')}
          chartType="horizontal_bar_chart"
          data={elementRows(elements, 'attainment')}
          tileKey={`${keyPrefix}-el-attainment`}
          tooltipKeys={{ label: 'Plan element', value: 'Attainment %' }}
          settings={{ ...HBAR, axisBottomLegend: 'Attainment %' }}
        />
        <CampaignChartCard
          title="Quota by plan element"
          subtitle={sub('USD, millions')}
          chartType="horizontal_bar_chart"
          data={elementRows(elements, 'quota')}
          tileKey={`${keyPrefix}-el-quota`}
          tooltipKeys={{ label: 'Plan element', value: 'USD (millions)' }}
          settings={{ ...HBAR, axisBottomLegend: 'USD (millions)' }}
        />
        <CampaignChartCard
          title="Bookings by plan element"
          subtitle={sub('USD, millions')}
          chartType="horizontal_bar_chart"
          data={elementRows(elements, 'bookings')}
          tileKey={`${keyPrefix}-el-bookings`}
          tooltipKeys={{ label: 'Plan element', value: 'USD (millions)' }}
          settings={{ ...HBAR, axisBottomLegend: 'USD (millions)' }}
        />
        <CampaignChartCard
          title="Bookings mix"
          subtitle={sub('share of bookings by plan element')}
          chartType="donut_chart"
          data={elementBookingsMixRows(elements)}
          tileKey={`${keyPrefix}-el-mix`}
          tooltipKeys={{ label: 'Plan element', value: 'USD (millions)' }}
          empty="No bookings in this interval."
        />
      </ChartGrid>
      <div style={{ marginTop: 12 }}>
        <HbrDomTable autoLayout={true} columns={columns} data={rows} style={{ display: 'block', width: '100%' }}>
          {columns.map((column) => (
            <div key={`column-${column.id}`} slot={`column-${column.id}`}>
              <div
                className="flex items-center"
                style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
              >
                <div>{column.name}</div>
              </div>
            </div>
          ))}
          {rows.flatMap((row) =>
            columns.map((column) => (
              <div
                key={`row-${row.id}-column-${column.id}`}
                slot={`row-${row.id}-column-${column.id}`}
                className="min-h-8 min-w-0 flex items-center box-content truncate"
                style={{
                  justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: row.isTotal ? 700 : undefined,
                }}
                title={(row as any)[column.id]}
              >
                {(row as any)[column.id]}
              </div>
            )),
          )}
        </HbrDomTable>
      </div>
    </div>
  );
}
